// biome-ignore-all lint/a11y: TODO - address a11y issues later
import type { ReactNode } from "react";
import { SavedViewProvider } from "@/contexts/saved-view-context";
import { ViewSelector } from "./view-selector";

interface ListPageLayoutProps {
  pageType: string;
  title: string;
  children: ReactNode;
  className?: string;
  headerClassName?: string;
  contentClassName?: string;
  /**
   * Additional content to render in the header, inline with the title.
   * Renders to the right of the title in the left-hand column.
   */
  headerExtras?: ReactNode;
  /**
   * Additional aria attributes for accessibility
   */
  ariaLabel?: string;
  headingId?: string;
  /**
   * Whether to hide the saved view selector.
   * Useful for pages that don't support saved views (e.g., rules management).
   */
  hideViewSelector?: boolean;
}

/**
 * ListPageLayout provides a consistent layout structure for all list pages.
 * It includes:
 * - SavedViewProvider context wrapper
 * - Consistent header with title and ViewSelector
 * - Responsive layout that works on all screen sizes
 * - Proper spacing and styling
 *
 * This eliminates the need to duplicate the entire layout structure across all list pages.
 */
export function ListPageLayout({
  pageType,
  title,
  children,
  className = "pr-4 max-w-full",
  headerClassName,
  contentClassName,
  headerExtras,
  ariaLabel,
  headingId,
  hideViewSelector = false,
}: ListPageLayoutProps) {
  return (
    <SavedViewProvider pageType={pageType}>
      <div
        className={`flex h-full flex-col space-y-4 ${className}`}
        role={ariaLabel ? "main" : undefined}
        aria-label={ariaLabel}
      >
        {/* Header Section */}
        <div className={`shrink-0 ${headerClassName || ""}`}>
          <div className="flex flex-col space-y-4 xl:flex-row xl:items-start xl:space-y-0 xl:space-x-4">
            <div className="flex-1">
              <div className="flex flex-col space-y-4 pt-4 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-4">
                <h1 id={headingId} className="font-heading">
                  {title}
                </h1>
                {headerExtras && (
                  <div className="flex items-center">{headerExtras}</div>
                )}
              </div>
            </div>
            {!hideViewSelector && (
              <div className="shrink-0 pt-4">
                <ViewSelector pageType={pageType} />
              </div>
            )}
          </div>
        </div>

        {/* Content Section (DataTable) */}
        <div
          className={
            contentClassName
              ? `min-h-0 flex-grow ${contentClassName}`
              : "min-h-0 flex-grow"
          }
        >
          {children}
        </div>
      </div>
    </SavedViewProvider>
  );
}
